import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

const STORAGE_KEY = "kompass.cookies";

const TEXTS = {
  ar: {
    title: "نستخدم ملفات تعريف الارتباط",
    body: "نستخدم تخزيناً تقنياً ضرورياً (لحفظ لغتك المختارة) وملفات تعريف خارجية لتحميل الخطوط والصور. لا نستخدم أي أدوات تتبع أو تسويق.",
    learn: "اعرف المزيد",
    accept: "موافق",
    decline: "رفض غير الضروري",
  },
  de: {
    title: "Cookies & lokaler Speicher",
    body: "Wir verwenden technisch notwendigen Speicher (z. B. für Ihre Sprachauswahl) sowie externe Inhalte (Schriftarten, Bilder). Es kommen keinerlei Tracking- oder Marketing-Cookies zum Einsatz.",
    learn: "Mehr erfahren",
    accept: "Verstanden",
    decline: "Nicht notwendige ablehnen",
  },
  en: {
    title: "We use cookies",
    body: "We use strictly necessary storage (e.g. to remember your language) and external resources for fonts and images. No tracking or marketing cookies are used.",
    learn: "Learn more",
    accept: "Got it",
    decline: "Reject non-essential",
  },
  fa: {
    title: "ما از کوکی استفاده می‌کنیم",
    body: "ما از حافظه فنی ضروری (مانند ذخیره زبان شما) و منابع خارجی برای فونت و تصاویر استفاده می‌کنیم. هیچ کوکی ردیابی یا بازاریابی استفاده نمی‌شود.",
    learn: "اطلاعات بیشتر",
    accept: "متوجه شدم",
    decline: "رد موارد غیرضروری",
  },
};

const CookieConsent = () => {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const decide = (choice) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() })
      );
    }
    setOpen(false);
  };

  const tx = TEXTS[lang] || TEXTS.de;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.35, ease: [0.2, 0.7, 0.3, 1] }}
          data-testid="cookie-consent"
          className="fixed inset-x-3 bottom-3 md:inset-x-auto md:bottom-5 md:end-5 md:max-w-md z-[60]"
        >
          <div
            className="rounded-2xl bg-white shadow-[0_24px_60px_-20px_rgba(31,107,122,0.45)] border border-[rgba(31,107,122,0.15)] overflow-hidden"
          >
            <div
              className="h-1 w-full"
              style={{
                background:
                  "linear-gradient(90deg, var(--kompass-primary) 0%, var(--kompass-primary-light) 60%, var(--kompass-accent) 100%)",
              }}
            />
            <div className="p-5 md:p-6">
              <div className="flex items-start gap-3">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                  style={{ background: "rgba(31,107,122,0.1)" }}
                >
                  <Cookie
                    className="h-5 w-5"
                    strokeWidth={1.6}
                    style={{ color: "var(--kompass-primary)" }}
                  />
                </span>
                <div className="flex-1">
                  <h3 className="text-[15px] font-semibold text-[color:var(--kompass-ink)]">
                    {tx.title}
                  </h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-[color:var(--kompass-ink-soft)]">
                    {tx.body}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="close"
                  onClick={() => decide("declined")}
                  data-testid="cookie-close"
                  className="text-[color:var(--kompass-muted)] hover:text-[color:var(--kompass-ink)] transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => decide("accepted")}
                  data-testid="cookie-accept"
                  className="inline-flex items-center gap-2 rounded-full bg-[color:var(--kompass-primary)] hover:bg-[color:var(--kompass-primary-dark)] text-white px-5 py-2.5 text-sm font-semibold transition-colors"
                >
                  <Check className="h-4 w-4" strokeWidth={2.2} />
                  {tx.accept}
                </button>
                <button
                  type="button"
                  onClick={() => decide("declined")}
                  data-testid="cookie-decline"
                  className="rounded-full border border-[rgba(31,107,122,0.2)] text-[color:var(--kompass-ink-soft)] hover:border-[color:var(--kompass-primary)] hover:text-[color:var(--kompass-primary)] px-5 py-2.5 text-sm font-medium transition-colors"
                >
                  {tx.decline}
                </button>
                <Link
                  to="/datenschutz"
                  data-testid="cookie-learn-more"
                  onClick={() => setOpen(false)}
                  className="text-sm font-medium text-[color:var(--kompass-primary)] hover:underline ms-auto"
                >
                  {tx.learn} →
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
